import { Suspense, useState } from 'react';

import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Pill } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { CreditCardActions } from './CreditCardActions';
import { CreditCardItem } from './creditCardItem/CreditCardItem';
import { DrawerContent, DrawerSlider } from './drawerSlider/DrawerSlider';
import { MobileAppWidget } from './MobileAppWidget';
import { QuickLinks } from './quickLinks/QuickLinks';
import { useGetCreditCardDashboardData } from './useGetCreditCardDashboardData';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../pageStructure/mainContentPending';
import { daysAgo } from '../helpers';
import { ReplacementMessage } from './drawerSlider/replaceCard/ReplacementMessage';

export const CreditCardDashboard = () => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<MainContentPending />}>
                <DashboardContent />
            </Suspense>
        </ErrorBoundary>
    );
};

const DashboardContent = () => {
    const [drawerContent, setDrawerContent] = useState<DrawerContent>(null);

    const {
        cardsToDisplay,
        accountCreatedAt,
        activeCard,
        availableCredit,
        totalOutstandingBalance
    } = useGetCreditCardDashboardData();

    const shouldDisplayAppWidget = accountCreatedAt > daysAgo(7);

    return (
        <div className="grid grid-cols-4 gap-3xl bg-structure-100 px-[50px] py-3xl max-sm:flex max-sm:flex-col max-sm:gap-2xl max-sm:px-xl max-sm:py-2xl min-xl:px-[150px]">
            <div className="col-span-full flex flex-row items-center max-sm:flex-col max-sm:items-start">
                {/* Dashboard title */}
                <Text className="m-0 text-3xl font-bold max-sm:text-xl">
                    <FormattedMessage
                        id="_.credit_card.dashboard.title"
                        defaultMessage="Your iwoca Credit Card account"
                    />
                </Text>

                <div className="mx-m max-sm:mx-0">
                    <Pill variant="green" size="small">
                        <FormattedMessage
                            id="_.credit_card.dashboard.active"
                            defaultMessage="Active"
                        />
                    </Pill>
                </div>
            </div>
            <div className="col-span-3 max-w-[1000px]">
                <ReplacementMessage />
                {/* Account Overview */}
                <div className="col-span-5 max-sm:col-span-full max-sm:mr-xl max-sm:pb-0">
                    <div className="flex flex-row gap-xl max-sm:flex-col">
                        {/* Account Balance */}
                        <div
                            className="mr-l flex flex-col justify-between pr-l max-sm:col-span-full"
                            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                            aria-label="account-balance"
                        >
                            <span>
                                <Text className="text-l font-med">
                                    <FormattedMessage
                                        id="_.credit_card.dashboard.account_balance"
                                        defaultMessage="Account balance"
                                    />
                                </Text>
                                <Text className="text-xs font-reg">
                                    <FormattedMessage
                                        id="_.credit_card.dashboard.account_balance_subtext"
                                        defaultMessage="Inc. any accrued interest at a rate of 35.4% APR."
                                    />
                                </Text>
                            </span>
                            <Text className="mt-xs text-2xl font-med">
                                <FormattedCurrency
                                    value={totalOutstandingBalance}
                                />
                            </Text>
                            <Text
                                className="mt-s text-xs font-med"
                                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                                aria-label="available-credit"
                            >
                                <FormattedMessage
                                    id="_.credit_card.dashboard.available_credit"
                                    defaultMessage="Available credit:"
                                />
                                <FormattedCurrency
                                    value={availableCredit}
                                    className="ml-s"
                                />
                            </Text>
                        </div>
                    </div>
                </div>
                {shouldDisplayAppWidget && <MobileAppWidget />}
                {cardsToDisplay.map((card) => (
                    <CreditCardItem
                        key={card.enfuce_card_id}
                        card={card}
                        setDrawerContent={setDrawerContent}
                    />
                ))}
                {activeCard && (
                    <CreditCardActions setDrawerContent={setDrawerContent} />
                )}
            </div>
            <div className="col-span-1">
                <QuickLinks setDrawerContent={setDrawerContent} />
            </div>
            <DrawerSlider
                content={drawerContent}
                onClose={() => setDrawerContent(null)}
            />
        </div>
    );
};
